import { useCallback, useEffect, useMemo, useState } from "react";
import type { ComponentType } from "react";
import { useNavigate } from "react-router-dom";
import { AGENT_MIS_REPORT, COMPANY_FIRST_NAME, DEALERPLANSLIST, DOMAIN_NAME } from "@/_constant";
import { Button, DialogBox, DialogBoxActions } from "@/_component/Common";
import { SharePopup } from "@/_component/shared";
import SignAgreement from "@/_component/onboardusers/planlist/agentlist/SignAgreement";
import { useAuthContext, useLanguageContext } from "@/hooks";
import ProfileCardContent from "./ProfileCardContent";
import ProfileCardHeader from "./ProfileCardHeader";
import ReferralDrawer from "./ReferralDrawer";
import { executeDashboardAction } from "./dashboard-profile-card.actions";
import { resolveDashboardCardConfig } from "./dashboard-profile-card.resolver";
import type { DashboardAction, DashboardProfile, DialogName, SideDrawerName } from "./types";
import { fetchReferralOnboardingCount, viewUserAccessMetadata } from "@/apis";
import "./dashboard-profile-card.css";

interface Props {
  selectedCategory: DashboardProfile;
  categories: DashboardProfile[];
  onSelectCategory: (categoryId: string) => void;
  recentPostThumbnail?: string | null;
  recentPostCount?: number;
}

const SignAgreementComponent = SignAgreement as unknown as ComponentType<{
  currentPlanId: string | null;
  userAccessId: string | null;
  toggleAgreementPopUpModal: () => void;
}>;

const DashboardProfileCard = ({ selectedCategory, categories, onSelectCategory, recentPostThumbnail, recentPostCount = 0 }: Props) => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { language } = useLanguageContext();
  const [referralOnboardingCount, setReferralOnboardingCount] = useState<any>({});
  const [dialog, setDialog] = useState<DialogName | SideDrawerName | null>(null);
  const [showShareLinkDialog, setShowShareLinkDialog] = useState<boolean>(false);
  const [agreementSigned, setAgreementSigned] = useState<boolean | null>(null);
  const config = useMemo(() => resolveDashboardCardConfig(selectedCategory.categoryId), [selectedCategory.categoryId]);
  const items = useMemo(() => categories.map((profile) => ({ profile, config: resolveDashboardCardConfig(profile.categoryId) })), [categories]);

  const loadAgreementStatus = useCallback(async () => {
    if (!config.requiresAgreement || !selectedCategory.userAccessId) {
      setAgreementSigned(null);
      return;
    }
    setAgreementSigned(null);
    try {
      const response = await viewUserAccessMetadata(selectedCategory.userAccessId);
      const metadata = response?.data?.response?.metadata ?? [];
      const agreement = metadata.find((item: { accessKey?: string }) => item.accessKey === "AgentAgreementLink");
      setAgreementSigned(Boolean(agreement?.accessValue?.trim()));
    } catch {
      setAgreementSigned(null);
    }
  }, [config.requiresAgreement, selectedCategory.userAccessId]);

  useEffect(() => { 
    loadAgreementStatus(); 
  }, [loadAgreementStatus]);

  useEffect(() => {
    const fetchOnboardingCount = async () => {
      try {
        const res = await fetchReferralOnboardingCount();
        setReferralOnboardingCount(res.data.response);
      } catch (_) {
        setReferralOnboardingCount({});
      }
      return {};
    }
    fetchOnboardingCount();
  }, []);

  const runAction = (action: DashboardAction) => executeDashboardAction(action, selectedCategory, navigate, setDialog);
  
  const closeAgreement = () => {
    setDialog(null);
    loadAgreementStatus();
  };

  const renewPlan = () => navigate(DEALERPLANSLIST, { state: JSON.stringify({
    planType: "CATEGORY",
    category_id: selectedCategory.categoryId,
    userAccessId: selectedCategory.userAccessId,
    currentUserPlanId: selectedCategory.userPlanId,
    paymentCategory: "RENEW_ONBOARDING_PLAN",
  }) });

  const Content = config.content ?? ProfileCardContent;
  const referralLink = `https://${DOMAIN_NAME}?ref=${user?.referralCode ?? ""}`;

  return (
    <section className="pt-4 pb-4">
      <div className="container px-2">
        <div className="card rounded-4 p-3 p-md-3 shadow-sm border border-secondary" style={{"backgroundColor": "#faecff"}}>
          <ProfileCardHeader
            selected={{ profile: selectedCategory, config }}
            items={items}
            agreementSigned={agreementSigned}
            onSelect={onSelectCategory}
            executeAction={runAction}
          />
          <Content
            profile={selectedCategory}
            config={config}
            recentPostThumbnail={recentPostThumbnail}
            recentPostCount={recentPostCount}
            executeAction={runAction}
          />
        </div>
      </div>

      {dialog === "SIGN_AGREEMENT" && (
        <DialogBox title="Sign Agreement" onClose={closeAgreement}>
          <SignAgreementComponent currentPlanId={selectedCategory.planId} userAccessId={selectedCategory.userAccessId} toggleAgreementPopUpModal={closeAgreement} />
        </DialogBox>
      )}
      {dialog === "RENEW_PLAN" && (
        <DialogBox title="Renew Plan" onClose={() => setDialog(null)}>
          <p className="text-center">Your plan has expired. Kindly renew it.</p>
          <DialogBoxActions>
            <Button warning solid rounded onClick={renewPlan}>Renew Plan</Button>
          </DialogBoxActions>
        </DialogBox>
      )}
      {dialog === 'ONBOARDING_COUNT' && (
        <DialogBox title="Onboarding | Ads" onClose={() => setDialog(null)}>
          <div className="d-flex flex-column justify-content-center gap-2">
            <div className="d-flex justify-content-between">
              <span>Customer</span>
              <span className="fw-bold">{referralOnboardingCount?.farmerOnboarding ?? 0}</span>
            </div>
            <div className="d-flex justify-content-between">
              <span>Free Onboarding</span>
              <span className="fw-bold">{referralOnboardingCount?.freeOnboarding ?? 0}</span>
            </div>
            <div className="d-flex justify-content-between">
              <span>Paid Onboarding</span>
              <span className="fw-bold">{referralOnboardingCount?.paidOnboarding ?? 0}</span>
            </div>
            <hr />
            <div className="d-flex justify-content-between">
              <span>Ads</span>
              <span className="fw-bold">{referralOnboardingCount?.adsCount ?? 0}</span>
            </div>
            <div className="d-flex justify-content-between">
              <span>Customer Rank</span>
              <span className="fw-bold">{referralOnboardingCount?.farmerRank ?? 0}</span>
            </div>
            <div className="d-flex justify-content-between">
              <span>Onboarding Rank</span>
              <span className="fw-bold">{referralOnboardingCount?.onboardingRank ?? 0}</span>
            </div>
            <hr />

            <DialogBoxActions>
              <Button
                primary
                outline
                rounded
                className="border-2"
                onClick={() => navigate(AGENT_MIS_REPORT)}
              >
                <i className="bi bi-file-bar-graph me-2"></i>
                {language("label.mis_report")}
              </Button>
            </DialogBoxActions>
          </div>
        </DialogBox>
      )}

      <ReferralDrawer
        isOpen={dialog === "REFERRAL"}
        referralLink={referralLink}
        onClose={() => setDialog(null)}
        onShare={() => setShowShareLinkDialog(true)}
      />
      
      {showShareLinkDialog && (
        <SharePopup
          onClose={() => setShowShareLinkDialog(false)}
          content={referralLink}
          title="Share Referral Link"
          subject={`Referral link to join ${COMPANY_FIRST_NAME}`}
        />
      )}
    </section>
  );
};

export default DashboardProfileCard;
